import { createRouter, createRoute } from "@tanstack/react-router";

import { Route as rootRoute } from "./routes/__root";
import LandingPage from "./routes/home";
import MyFirstMarathonPage from "./routes/myfirstmarathon";
import SearchPage from "./routes/index";
import AboutPage from "./routes/about";
import BlogPage from "./routes/blog";
import ChangelogPage from "./routes/changelog";
import AthletePage from "./routes/athletes.$athleteId";
import EventPage from "./routes/events.$eventId";
import EventSeriesPage from "./routes/events.series.$seriesId";
import EventGroupPage from "./routes/events.group.$groupId";
import SchedulePage from "./routes/schedule.$collectionKey";
import PrivacyPolicyPage from "./routes/privacy-policy";
import ResultPage from "./routes/results.$resultId";
import NewsletterPage from "./routes/newsletter";
import RequestRacePage from "./routes/request-race";
import SupportPage from "./routes/support";
import TermsOfServicePage from "./routes/terms-of-service";
import ComparePage from "./routes/compare";
import SignUpPage from "./routes/sign-up";
import SignInPage from "./routes/sign-in";
import ProfilePage from "./routes/profile";
import AdminPage from "./routes/admin";

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

const myFirstMarathonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "myfirstmarathon",
  component: MyFirstMarathonPage,
});

const recordsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "records",
  component: SearchPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    type?: "athletes" | "events";
    gender?: string;
    raceType?: string;
    brand?: string;
    swimCourse?: string;
    bikeCourse?: string;
    runCourse?: string;
  } => ({
    q: (search.q as string) || undefined,
    type: (search.type as "athletes" | "events") || undefined,
    gender: (search.gender as string) || undefined,
    raceType: (search.raceType as string) || undefined,
    brand: (search.brand as string) || undefined,
    swimCourse: (search.swimCourse as string) || undefined,
    bikeCourse: (search.bikeCourse as string) || undefined,
    runCourse: (search.runCourse as string) || undefined,
  }),
});

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "about",
  component: AboutPage,
});

const blogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "blog",
  component: BlogPage,
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "changelog",
  component: ChangelogPage,
});

const termsOfServiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "terms-of-service",
  component: TermsOfServicePage,
});

const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "privacy-policy",
  component: PrivacyPolicyPage,
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "support",
  component: SupportPage,
});

const requestRaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "request-race",
  component: RequestRacePage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    race?: string;
    source?: "home" | "records";
  } => ({
    race: (search.race as string) || undefined,
    source:
      search.source === "home" || search.source === "records"
        ? search.source
        : undefined,
  }),
});

const newsletterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "newsletter",
  component: NewsletterPage,
});

const athleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "athletes/$athleteId",
  component: AthletePage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: "overview" | "progression" | "results";
  } => ({
    tab: (["overview", "progression", "results"] as const).includes(
      search.tab as "overview" | "progression" | "results",
    )
      ? (search.tab as "overview" | "progression" | "results")
      : undefined,
  }),
});

const eventRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "events/$eventId",
  component: EventPage,
  validateSearch: (search: Record<string, unknown>) => ({
    ageGroup: (search.ageGroup as string) || undefined,
    highlight: (search.highlight as string) || undefined,
    highlightRank:
      typeof search.highlightRank === "number"
        ? search.highlightRank
        : typeof search.highlightRank === "string"
          ? Number(search.highlightRank) || undefined
          : undefined,
    page:
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? Number(search.page) || undefined
          : undefined,
  }),
});

const eventSeriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "events/series/$seriesId",
  component: EventSeriesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    year:
      typeof search.year === "number"
        ? search.year
        : typeof search.year === "string"
          ? Number(search.year) || undefined
          : undefined,
  }),
});

const eventGroupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "events/group/$groupId",
  component: EventGroupPage,
});

const scheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "schedule/$collectionKey",
  component: SchedulePage,
});

const resultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "results/$resultId",
  component: ResultPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-up",
  component: SignUpPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  component: SignInPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "profile",
  component: ProfilePage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin",
  component: AdminPage,
});

const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "compare",
  component: ComparePage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    a?: string;
    b?: string;
  } => ({
    a: (search.a as string) || undefined,
    b: (search.b as string) || undefined,
  }),
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  myFirstMarathonRoute,
  aboutRoute,
  blogRoute,
  changelogRoute,
  termsOfServiceRoute,
  privacyPolicyRoute,
  supportRoute,
  requestRaceRoute,
  newsletterRoute,
  recordsRoute,
  athleteRoute,
  eventRoute,
  eventSeriesRoute,
  eventGroupRoute,
  scheduleRoute,
  resultRoute,
  compareRoute,
  signUpRoute,
  signInRoute,
  profileRoute,
  adminRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
